import type WaButton from "@web.awesome.me/webawesome-pro/dist/components/button/button.js";
import type WaInput from "@web.awesome.me/webawesome-pro/dist/components/input/input.js";
import htmx from "htmx.org";
import { css, html } from "lit";
import { checkNotNil } from "../../check-not-nil";
import { type FormSubmissionHandler, formSubmissionHandler } from "../../form";
import { JsonBootstrapper } from "../../handlers/json-bootstrapper";
import { Metric } from "../../schemas/metrics.js";
import {
  type ParserSchema,
  type ParserType,
  parser,
} from "../../schemas/parser";
import { ValidationError } from "../../schemas/validation-error";
import { hostStyles } from "../../styles/host.styles";
import { WmElement } from "../element";

type Payload = ParserType<typeof Payload>;
const Payload = parser(Metric.schema);

export class WmMetricEdit extends WmElement {
  static readonly TAG_NAME = "wm-metric-edit";

  static styles = [
    hostStyles,
    css`
      form {
        display: flex;
        flex-direction: column;
        align-items: stretch;
        justify-content: flex-start;
        gap: var(--wa-space-m);
      }

      wa-button[type="submit"] {
        align-self: start;
      }
    `,
  ];

  #formSubmit?: FormSubmissionHandler;
  #payload: Payload;
  #jsonBootstrapper: JsonBootstrapper<ParserSchema<typeof Payload>>;

  constructor() {
    super();
    this.#formSubmit = undefined;
    this.#payload = {
      id: "",
      name: "",
      tags: [],
      definition: { kind: "", defaultUnit: "" },
    };
    this.#jsonBootstrapper = new JsonBootstrapper({
      schema: Payload.schema,
      callback: (v) => {
        this.#payload = v;
        this.#formSubmit = formSubmissionHandler(`/app/metrics/${v.id}`, {
          method: "PUT",
        })
          .on("pre:response", () => (this.#querySubmitButton().loading = true))
          .on("response", (r) => this.#handleResponse(r))
          .on(
            "post:response",
            () => (this.#querySubmitButton().loading = false),
          )
          .on("error", (err) =>
            this.logger.error("unable to PUT /app/metrics", err),
          );
        this.requestUpdate();
      },
      logger: this.logger,
    });
  }

  protected override render() {
    const availableTags = this.#payload.tags;
    const selectedTagIds = availableTags.map((t) => t.id);

    this.logger.info("rendering", this.#formSubmit);
    return html`
      <slot @slotchange=${(e: Event) => this.#jsonBootstrapper.slotChanged(e)}></slot>
      <form @submit=${{ handleEvent: this.#formSubmit }}>
        <wm-output class="id" label="Id">${this.#payload.id}</wm-output>
        <wa-input
          label="Name"
          name="name"
          required
          .value=${this.#payload.name}
          @input=${this.#handleNameInput}>
        </wa-input>
        <wm-metric-definition
          .kind=${this.#payload.definition.kind}
          .defaultUnit=${this.#payload.definition.defaultUnit}>
        </wm-metric-definition>
        <wm-tags-combobox
          name="tags"
          .availableTags=${availableTags}
          .selectedTagIds=${selectedTagIds}>
        </wm-tags-combobox>
        <wa-button variant="success" type="submit">Modify</wa-button>
      </form>
    `;
  }

  #handleNameInput(e: InputEvent) {
    e.stopPropagation();
    const input = checkNotNil(e.target) as WaInput;
    // Reset the custom validity, if any was set before.
    input.setCustomValidity("");
  }

  async #handleResponse(response: Response) {
    if (response.status === 422) {
      const validationError = ValidationError.parse(await response.json());
      const { path, reason } = validationError.error;

      if (path === "name") {
        const input = this.#queryNameInput();
        input.setCustomValidity(reason);
        input.reportValidity();
      } else {
        this.logger.warn(
          "unknown invalid field from 422 response: %s",
          path,
          validationError,
        );
      }
    } else {
      return htmx.ajax("GET", "/app/metrics", { push: "true" });
    }
  }

  #queryNameInput(): WaInput {
    return this.queryNotNil(`wa-input[name="name"]`);
  }

  #querySubmitButton(): WaButton {
    return this.queryNotNil(`wa-button[type="submit"]`);
  }
}

customElements.define(WmMetricEdit.TAG_NAME, WmMetricEdit);

declare global {
  interface HTMLElementTagNameMap {
    [WmMetricEdit.TAG_NAME]: WmMetricEdit;
  }
}
